import { 
  collection, 
  addDoc, 
  getDocs, 
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  query, 
  where, 
  orderBy, 
  Timestamp,
  QueryConstraint
} from 'firebase/firestore';
import { db } from '../config/firebase.config';
import { MoldHistory, HistoryFilter, HistoryFormData, HistoryFile } from '../types/history.types';
import storageService from './storage.service';
import { moldService } from './mold.service';

export class MoldHistoryService {
  private readonly collectionName = 'mold_histories';

  // 이력 등록
  async create(data: HistoryFormData, files?: File[]): Promise<string> {
    try {
      const now = Timestamp.now();
      
      // 금형 정보 조회 (moldNumber 가져오기)
      const mold = await moldService.getMoldById(data.moldId);
      const moldNumber = mold?.moldNumber || '';
      const moldName = mold?.moldName || '';
      
      // 파일 업로드 처리
      let uploadedFiles: HistoryFile[] = [];
      if (files && files.length > 0) {
        uploadedFiles = await this.uploadFiles(data.moldId, files);
      }
      
      // undefined 필드 제거를 위한 기본 데이터
      const historyData: any = {
        moldId: data.moldId,
        moldNumber,    // 조회한 금형번호
        moldName,      // 조회한 금형명
        type: data.type,
        title: data.title,
        description: data.description,
        date: Timestamp.fromDate(data.date),
        files: uploadedFiles,
        userId: '',  // Auth context에서 채워짐
        createdAt: now,
        updatedAt: now
      };

      // 선택적 필드 추가 (undefined 제외)
      if (data.shots !== undefined) historyData.shots = data.shots;
      if (data.purpose !== undefined) historyData.purpose = data.purpose;
      if (data.expectedReturn !== undefined) historyData.expectedReturn = Timestamp.fromDate(data.expectedReturn);
      if (data.productionQty !== undefined) historyData.productionQty = data.productionQty;
      if (data.defectQty !== undefined) historyData.defectQty = data.defectQty;
      if (data.selectedCavities !== undefined) historyData.selectedCavities = data.selectedCavities;
      if (data.cost !== undefined) historyData.cost = data.cost;
      if (data.vendor !== undefined) historyData.vendor = data.vendor;
      if (data.inspector !== undefined) historyData.inspector = data.inspector;
      if (data.inspectionResult !== undefined) historyData.inspectionResult = data.inspectionResult;
      if (data.disposalReason !== undefined) historyData.disposalReason = data.disposalReason;
      if (data.disposalMethod !== undefined) historyData.disposalMethod = data.disposalMethod;

      const docRef = await addDoc(collection(db, this.collectionName), historyData);
      return docRef.id;
    } catch (error) {
      console.error('이력 등록 실패:', error);
      throw new Error('이력 등록에 실패했습니다.');
    }
  }

  // 금형별 이력 조회
  async getByMoldId(moldId: string): Promise<MoldHistory[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('moldId', '==', moldId),
        orderBy('date', 'desc')
      );

      const snapshot = await getDocs(q);
      return this.convertDocs(snapshot.docs);
    } catch (error) {
      console.error('금형별 이력 조회 실패:', error);
      throw new Error('이력 조회에 실패했습니다.');
    }
  }

  // 전체 이력 조회 (필터링)
  async getAll(filter?: HistoryFilter): Promise<MoldHistory[]> {
    try {
      const constraints: QueryConstraint[] = [];

      if (filter?.moldId) {
        constraints.push(where('moldId', '==', filter.moldId));
      }
      if (filter?.type) {
        constraints.push(where('type', '==', filter.type));
      }
      if (filter?.startDate) {
        constraints.push(where('date', '>=', Timestamp.fromDate(filter.startDate)));
      }
      if (filter?.endDate) {
        constraints.push(where('date', '<=', Timestamp.fromDate(filter.endDate)));
      }

      constraints.push(orderBy('date', 'desc'));

      const q = query(collection(db, this.collectionName), ...constraints);
      const snapshot = await getDocs(q);
      
      let results = this.convertDocs(snapshot.docs);
      
      // 텍스트 검색 (클라이언트 사이드)
      if (filter?.searchText) {
        const search = filter.searchText.toLowerCase();
        results = results.filter(h => 
          h.moldNumber.toLowerCase().includes(search) ||
          h.title.toLowerCase().includes(search) ||
          h.description.toLowerCase().includes(search)
        );
      }

      return results;
    } catch (error) {
      console.error('이력 조회 실패:', error);
      throw new Error('이력 조회에 실패했습니다.');
    }
  }

  // 단일 이력 조회
  async getById(id: string): Promise<MoldHistory | null> {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return this.convertDoc(docSnap);
    } catch (error) {
      console.error('이력 조회 실패:', error);
      throw new Error('이력 조회에 실패했습니다.');
    }
  }

  // 이력 수정
  async update(id: string, data: Partial<HistoryFormData>, files?: File[], existingFiles?: HistoryFile[]): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, id);
      
      // 새 파일 업로드
      let uploadedFiles: HistoryFile[] = [];
      if (files && files.length > 0) {
        const existing = await this.getById(id);
        uploadedFiles = await this.uploadFiles(existing?.moldId || '', files);
      }

      // 기존 파일 + 새 파일 합치기
      const allFiles = [...(existingFiles || []), ...uploadedFiles];

      // 기본 업데이트 데이터 (undefined 필드 제거)
      const updateData: any = {
        files: allFiles,
        updatedAt: Timestamp.now()
      };

      // 필수 필드 (undefined 아닐 때만 추가)
      if (data.type !== undefined) updateData.type = data.type;
      if (data.title !== undefined) updateData.title = data.title;
      if (data.description !== undefined) updateData.description = data.description;
      if (data.date !== undefined) updateData.date = Timestamp.fromDate(data.date);

      // 선택적 필드 (undefined 제외)
      if (data.shots !== undefined) updateData.shots = data.shots;
      if (data.purpose !== undefined) updateData.purpose = data.purpose;
      if (data.expectedReturn !== undefined) updateData.expectedReturn = Timestamp.fromDate(data.expectedReturn);
      if (data.productionQty !== undefined) updateData.productionQty = data.productionQty;
      if (data.defectQty !== undefined) updateData.defectQty = data.defectQty;
      if (data.selectedCavities !== undefined) updateData.selectedCavities = data.selectedCavities;
      if (data.cost !== undefined) updateData.cost = data.cost;
      if (data.vendor !== undefined) updateData.vendor = data.vendor;
      if (data.inspector !== undefined) updateData.inspector = data.inspector;
      if (data.inspectionResult !== undefined) updateData.inspectionResult = data.inspectionResult;
      if (data.disposalReason !== undefined) updateData.disposalReason = data.disposalReason;
      if (data.disposalMethod !== undefined) updateData.disposalMethod = data.disposalMethod;

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('이력 수정 실패:', error);
      throw new Error('이력 수정에 실패했습니다.');
    }
  }

  // 이력 삭제
  async delete(id: string): Promise<void> {
    try {
      // 파일 먼저 삭제
      const history = await this.getById(id);
      if (history?.files && history.files.length > 0) {
        for (const file of history.files) {
          await storageService.deleteFile(file.url);
        }
      }

      await deleteDoc(doc(db, this.collectionName, id));
    } catch (error) {
      console.error('이력 삭제 실패:', error);
      throw new Error('이력 삭제에 실패했습니다.');
    }
  }

  // 파일 업로드 헬퍼
  private async uploadFiles(moldId: string, files: File[]): Promise<HistoryFile[]> {
    const uploadedFiles: HistoryFile[] = [];
    
    for (const file of files) {
      const path = `histories/${moldId}/${Date.now()}_${file.name}`;
      const url = await storageService.uploadFile(path, file);
      
      uploadedFiles.push({
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        name: file.name,
        url,
        type: file.type.startsWith('image/') ? 'image' : 'document',
        size: file.size,
        uploadedAt: new Date()
      });
    }
    
    return uploadedFiles;
  }

  // Firestore 문서 변환
  private convertDoc(docSnap: any): MoldHistory {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      date: data.date?.toDate(),
      expectedReturn: data.expectedReturn?.toDate(),
      actualReturn: data.actualReturn?.toDate(),
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      files: data.files?.map((f: any) => ({
        ...f,
        uploadedAt: f.uploadedAt?.toDate?.() || new Date()
      }))
    } as MoldHistory;
  }


  private convertDocs(docs: any[]): MoldHistory[] {
    return docs.map(doc => this.convertDoc(doc));
  }

  // 기간별 통계
  async getStats(startDate: Date, endDate: Date) {
    try {
      const histories = await this.getAll({
        startDate,
        endDate
      });

      const stats = {
        total: histories.length,
        byType: {} as Record<string, number>,
        totalCost: 0,
        avgCost: 0
      };

      histories.forEach(h => {
        stats.byType[h.type] = (stats.byType[h.type] || 0) + 1;
        if (h.cost) {
          stats.totalCost += h.cost;
        }
      });

      const costCount = histories.filter(h => h.cost).length;
      stats.avgCost = costCount > 0 ? stats.totalCost / costCount : 0;

      return stats;
    } catch (error) {
      console.error('통계 조회 실패:', error);
      throw new Error('통계 조회에 실패했습니다.');
    }
  }
}

export const moldHistoryService = new MoldHistoryService();
